/** Distributions by height, height layer, DBH and volume: a dot map beside each chart. */
import { memo, useMemo, useState, type ReactNode } from 'react';
import { BarChart } from '@mantine/charts';
import { ResponsiveContainer } from 'recharts';
import { Card, Checkbox, Group, SimpleGrid, Stack, Tabs, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useFormat } from '@/components/useFormat';
import { CHART, STRATA, speciesColor, viridis } from '@/theme/colors';
import { CHART_AXIS_PROPS } from './charts';
import type { AnalysisTab } from './config';
import { DotMap } from './DotMap';
import { Section } from './Section';
import { useSpeciesName } from './species';
import { SpeciesMiniMaps } from './SpeciesMiniMaps';
import { usePrinting } from './usePrinting';
import { dbhClasses, HISTOGRAM_BINS, histogram, metricOf, metricRange, once, OTHER, stratumOf, type Stacked, type TreeStats } from './stats';
import type { Metric, Stratum, Tree } from './types';

const LAYERS: Stratum[] = ['upper', 'middle', 'lower'];
const UNIT: Record<Metric, string> = { height: 'm', dbh: 'cm', volume: 'm³' };
const CHART_HEIGHT = 280;
/** Fits the printable width of an A4 page at the report's margins. */
export const PRINT_CHART_WIDTH = 640;

/**
 * On paper a chart gets a fixed size: a chart that was hidden on screen has never been measured,
 * and there is no resize before the page is captured.
 */
function ChartFrame({ printing, children }: { printing: boolean; children: ReactNode }) {
  if (!printing) return children;
  return (
    <ResponsiveContainer width={PRINT_CHART_WIDTH} height={CHART_HEIGHT}>
      {children}
    </ResponsiveContainer>
  );
}

function StackedBars({ stacked, label, printing }: { stacked: Stacked; label: (row: Stacked['rows'][number]) => string; printing: boolean }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const name = useSpeciesName();
  const data = stacked.rows.map((r) => ({ bin: label(r), ...r.counts }));
  const series = stacked.series.map((g) => ({ name: g, color: g === OTHER ? CHART.other : speciesColor(g), label: g === OTHER ? t('trees.analysis.other') : name(g) }));
  return (
    <ChartFrame printing={printing}>
      <BarChart h={CHART_HEIGHT} data={data} dataKey="bin" type="stacked" series={series} withLegend legendProps={{ verticalAlign: 'bottom' }} valueFormatter={(v) => number(v)} {...CHART_AXIS_PROPS} gridAxis="y" />
    </ChartFrame>
  );
}

// Memoized, as are the other tabs: a hidden tab stays mounted and must not re-render on every switch.
const MetricTab = memo(function MetricTab({ stats, metric, printing }: { stats: TreeStats; metric: Metric; printing: boolean }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const digits = metric === 'volume' ? 2 : 0;
  const [min, max] = useMemo(() => stats.derived?.ranges[metric] ?? metricRange(stats.trees, metric), [stats, metric]);
  const colorOf = useMemo(
    () => (x: Tree) => {
      const v = metricOf(x, metric);
      return v === null ? null : viridis((v - min) / (max - min || 1));
    },
    [metric, min, max],
  );
  const hist = useMemo(() => stats.derived?.histograms[metric] ?? once(stats.trees, `hist:${metric}`, () => histogram(stats.trees, metric, HISTOGRAM_BINS[metric])), [stats, metric]);
  const classes = useMemo(() => (metric === 'dbh' ? (stats.derived?.dbhClasses ?? once(stats.trees, 'dbhClasses', () => dbhClasses(stats.trees))) : null), [stats, metric]);
  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Card>
          <DotMap trees={stats.trees} colorOf={colorOf} label={t(`trees.analysis.tabs.${metric}`)} />
        </Card>
        <Card>
          <Text fw={700} mb="xs">
            {t('trees.analysis.distribution', { unit: UNIT[metric] })}
          </Text>
          <StackedBars stacked={hist} label={(r) => `${number(r.from, digits)}–${number(r.to, digits)}`} printing={printing} />
        </Card>
      </SimpleGrid>
      {classes && (
        <Card>
          <Text fw={700} mb="xs">
            {t('trees.analysis.dbhClasses')}
          </Text>
          <StackedBars stacked={classes} label={(r) => (r.to === Infinity ? `${number(r.from, 0)}+` : r.from === 0 ? `<${number(r.to, 0)}` : `${number(r.from, 0)}–${number(r.to, 0)}`)} printing={printing} />
        </Card>
      )}
      <SpeciesMiniMaps trees={stats.trees} metric={metric} />
    </Stack>
  );
});

const StrataTab = memo(function StrataTab({ stats, printing }: { stats: TreeStats; printing: boolean }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const [shown, setShown] = useState<string[]>(LAYERS);
  const s = stats.strata;
  const colorOf = useMemo(
    () => (x: Tree) => {
      if (!s || x.height === null) return null;
      const layer = stratumOf(x.height, s);
      return shown.includes(layer) ? STRATA[layer] : null;
    },
    [s, shown],
  );
  if (!s) return <Text>{t('trees.analysis.noHeights')}</Text>;
  const range: Record<Stratum, string> = {
    lower: `${number(s.min, 1)}–${number(s.lo, 1)} m`,
    middle: `${number(s.lo, 1)}–${number(s.hi, 1)} m`,
    upper: `${number(s.hi, 1)}–${number(s.max, 1)} m`,
  };
  const data = LAYERS.map((l) => ({ layer: `${t(`trees.analysis.strata.${l}`)} (${range[l]})`, [l]: s.counts[l] }));
  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
      <Card>
        <Checkbox.Group value={shown} onChange={setShown} className="report-controls" mb="xs">
          <Group gap="md">
            {LAYERS.map((l) => (
              <Checkbox key={l} value={l} label={t(`trees.analysis.strata.${l}`)} />
            ))}
          </Group>
        </Checkbox.Group>
        <DotMap trees={stats.trees} colorOf={colorOf} label={t('trees.analysis.tabs.strata')} />
      </Card>
      <Card>
        <ChartFrame printing={printing}>
          <BarChart
            h={CHART_HEIGHT}
            data={data}
            dataKey="layer"
            type="stacked"
            series={LAYERS.map((l) => ({ name: l, color: STRATA[l], label: t(`trees.analysis.strata.${l}`) }))}
            withLegend={false}
            valueFormatter={(v) => number(v)}
            {...CHART_AXIS_PROPS}
            gridAxis="y"
          />
        </ChartFrame>
        <Text fz={12} mt="xs">
          {LAYERS.map((l) => `${t(`trees.analysis.strata.${l}`)}: ${range[l]}`).join(' · ')}
        </Text>
      </Card>
    </SimpleGrid>
  );
});

export const AnalysisSection = memo(function AnalysisSection({ stats, tabs }: { stats: TreeStats; tabs: AnalysisTab[] }) {
  const { t } = useTranslation('reports');
  const [active, setActive] = useState<string | null>(tabs[0] ?? null);
  const current = active && tabs.includes(active as AnalysisTab) ? active : (tabs[0] ?? null);
  const printing = usePrinting();
  // A tab once shown keeps its maps and charts: drawing them again is what makes a switch slow.
  const [shown, setShown] = useState<string[]>([]);
  if (current && !shown.includes(current)) setShown([...shown, current]);
  return (
    <Section title={t('trees.sections.analysis')} className="report-analysis">
      {/* Every panel stays in the page for print CSS to unhide; on screen only the shown ones have content.
          `display-none`, not Mantine's default `<Activity>`, which would redraw a panel on every return. */}
      <Tabs value={current} onChange={setActive} keepMounted keepMountedMode="display-none">
        <Tabs.List mb="md">
          {tabs.map((tab) => (
            <Tabs.Tab key={tab} value={tab}>
              {t(`trees.analysis.tabs.${tab}`)}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        {tabs.map((tab) => (
          <Tabs.Panel key={tab} value={tab}>
            {(shown.includes(tab) || tab === current || printing) && (
              <>
                {/* Printed pages have no tabs to click, so each panel carries its own heading there. */}
                <Text className="print-only" fw={700} mb="xs">
                  {t(`trees.analysis.tabs.${tab}`)}
                </Text>
                {tab === 'strata' ? <StrataTab stats={stats} printing={printing} /> : <MetricTab stats={stats} metric={tab} printing={printing} />}
              </>
            )}
          </Tabs.Panel>
        ))}
      </Tabs>
    </Section>
  );
});
